import { useMemo } from 'react';
import { EMPTY_STATE_TYPE } from 'framework-listing-lib/constants/emptyState';
import useCrmObjectTypeListingFilters from 'framework-listing-lib/hooks/useCrmObjectTypeListingFilters';
import useFetchCrmObjectType from 'framework-listing-lib/hooks/useFetchCrmObjectType';
import { useFilterGroups } from 'framework-listing-lib/hooks/useListingLibFilters';
import { FOLDER_ID } from 'framework-listing-lib/internal/Folders/constants/folders';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import useFetchEmptyStateParams from './useFetchEmptyStateParams';
import useFetchFromCRM from './useFetchFromCRM';
import { useIsFoldersMode } from './useUIMode';
export default function useCrmObjectTypeListingEmptyState() {
  const objectType = useObjectTypeId();
  const {
    data
  } = useFetchCrmObjectType();
  const {
    filters
  } = useCrmObjectTypeListingFilters();
  const filterGroups = useFilterGroups();
  const isFoldersMode = useIsFoldersMode();
  const {
    variables: currentEmptyStateVariables
  } = useFetchEmptyStateParams();
  const {
    data: initialStateData,
    loading: loadingInitialState
  } = useFetchFromCRM(currentEmptyStateVariables);
  const hasInitialStateResults = Boolean(initialStateData && initialStateData.total > 0);
  const hasCurrentSearchResults = Boolean(data && data.total > 0);
  const hasAnyData = hasInitialStateResults || hasCurrentSearchResults;
  const emptyStateType = useMemo(() => {
    if (data === undefined || loadingInitialState || hasCurrentSearchResults) {
      return undefined;
    }
    if (!hasAnyData) {
      return EMPTY_STATE_TYPE.FIRST_TIME_IN_APP;
    }
    if (isFoldersMode) {
      const hasFiltersOtherThanFolder = filterGroups.some(filterGroup => filterGroup.filters.some(filter => filter.property !== FOLDER_ID));
      if (filters.query || hasFiltersOtherThanFolder) {
        return EMPTY_STATE_TYPE.NO_RESULTS_WITH_FILTER_APPLIED;
      }
      return EMPTY_STATE_TYPE.NO_RESULTS_IN_FOLDER;
    }
    return EMPTY_STATE_TYPE.NO_RESULTS_WITH_FILTER_APPLIED;
  }, [data, loadingInitialState, hasCurrentSearchResults, hasAnyData, isFoldersMode, filterGroups, filters.query]);
  const hasEmptyState = (() => {
    if (!data) {
      return undefined;
    }
    return data.total === 0;
  })();
  const isPending = (() => {
    if (!data) {
      // Waiting for the full crm-search query to resolve
      // In theory isPending could be false as soon as
      // the empty state query resolves with zero resolts
      // But with the current implementation, emptyStateType would still be undefined
      return true;
    }
    if (data.total === 0 && loadingInitialState) {
      // Definitely some kind of empty state type
      // waiting for the empty state query to resolve to tell which kind
      return true;
    }
    // Either both queries have resolved
    // or the empty state query is loading but the main query has results
    return false;
  })();
  const value = useMemo(() => ({
    emptyStateType,
    hasEmptyState,
    objectType,
    isPending
  }), [emptyStateType, hasEmptyState, isPending, objectType]);
  return value;
}